import { Clock, RotateCcw, ShieldCheck, Star } from "lucide-react";
import { useLandingCopy } from "./LandingContext";
import { AppStoreButton } from "./AppStoreButton";

const BENEFIT_ICONS = [Clock, RotateCcw, ShieldCheck];

/** Social proof — App Store-style review cards: star row, bold headline,
 *  quote. White cards on cream; single tight column on mobile. */
export function ParentsTestimonials() {
  const copy = useLandingCopy();

  return (
    <section className="py-12 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <h2 className="text-center font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {copy.testimonials.title}
        </h2>
        <div className="mt-6 grid gap-3 sm:mt-10 sm:grid-cols-3 sm:gap-5">
          {copy.testimonials.items.map((item) => (
            <figure
              key={item.name}
              className="flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6"
            >
              <span className="flex items-center gap-0.5 text-accent" aria-hidden="true">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </span>
              <p className="mt-2.5 font-heading text-base font-bold text-foreground">
                {item.title}
              </p>
              <blockquote className="mt-1.5 flex-1 text-base leading-relaxed text-foreground/80 sm:text-[15px]">
                {item.quote}
              </blockquote>
              <figcaption className="mt-4 text-sm">
                <span className="font-semibold text-foreground">{item.name}</span>
                <span className="text-muted-foreground"> · {item.detail}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Mid-page CTA — catches visitors convinced by the testimonials without
 *  making them scroll to the bottom of the page. */
export function ParentsMidCta() {
  const copy = useLandingCopy();

  return (
    <section className="pb-12 sm:pb-20">
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 px-4 text-center sm:gap-5 sm:px-6">
        <p className="font-heading text-xl font-semibold text-foreground sm:text-2xl">
          {copy.midCta.text}
        </p>
        <AppStoreButton
          location="mid_cta"
          label={copy.midCta.cta}
          variant="button"
          className="min-h-[52px] w-full px-7 py-4 text-lg sm:w-auto"
        />
      </div>
    </section>
  );
}

/** How it works — three numbered steps. */
export function ParentsHowItWorks() {
  const copy = useLandingCopy();

  return (
    <section className="py-12 sm:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <h2 className="text-center font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {copy.how.title}
        </h2>
        <div className="mt-6 grid gap-4 sm:mt-12 sm:grid-cols-3 sm:gap-6">
          {copy.how.steps.map((step, i) => (
            <div key={step} className="flex items-start gap-4 sm:flex-col sm:items-center sm:text-center">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary font-heading text-lg font-bold text-primary-foreground">
                {i + 1}
              </div>
              <p className="pt-1.5 text-[15px] leading-relaxed text-foreground/80 sm:pt-0">{step}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** "Why download ReFound?" — the three benefit cards, nothing more. */
export function ParentsWhy() {
  const copy = useLandingCopy();

  return (
    <section className="py-12 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <h2 className="text-center font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {copy.benefits.title}
        </h2>
        <div className="mt-6 grid gap-3 sm:mt-10 sm:grid-cols-3 sm:gap-5">
          {copy.benefits.items.map((item, i) => {
            const Icon = BENEFIT_ICONS[i] ?? Clock;
            return (
              <div
                key={item.title}
                className="rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6"
              >
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary sm:mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground sm:text-lg">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** The repeated App Store call-to-action near the bottom — flat and centered. */
export function ParentsSecondCta() {
  const copy = useLandingCopy();

  return (
    <section className="py-12 sm:py-20">
      <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
        <h2 className="font-heading text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
          {copy.secondCta.title}
        </h2>
        <div className="mt-6 flex flex-col items-center gap-4 sm:mt-8">
          <AppStoreButton
            location="second_cta"
            label={copy.secondCta.cta}
            variant="button"
            className="min-h-[52px] w-full px-7 py-4 text-lg sm:w-auto"
          />
          {/* Rating repeated at the decision point */}
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-0.5 text-accent" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-current" />
              ))}
            </span>
            <span className="font-semibold text-foreground">{copy.rating.value}</span>
            {copy.rating.label}
          </span>
        </div>
      </div>
    </section>
  );
}

/** Short FAQ. Native <details>: answers always in the DOM
 *  (crawlable, zero-JS, fast) and accessible. Driven entirely by data. */
export function ParentsFaq() {
  const copy = useLandingCopy();

  return (
    <section className="py-12 sm:py-20">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <h2 className="text-center font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
          {copy.faq.title}
        </h2>
        <div className="mt-6 flex flex-col gap-2.5 sm:mt-10">
          {copy.faq.items.map((item) => (
            <details
              key={item.q}
              className="group rounded-xl border border-border bg-card px-5 py-4 shadow-sm [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex min-h-[28px] cursor-pointer list-none items-center justify-between gap-4 text-[15px] font-semibold text-foreground">
                {item.q}
                <span className="text-lg leading-none text-muted-foreground transition-transform duration-200 group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
